/**
 * UpgradeModal — Patch 25 Part V.
 *
 * Never hard-fails. When a feature is blocked by an entitlement check
 * the caller opens this modal via ``useUpgradePrompt().open({...})``.
 * The customer sees three paths — upgrade plan, buy add-on, or wait
 * for the monthly reset — and never sees provider names, dollar costs,
 * or infrastructure copy.
 */
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";

import { useUpgradePrompt } from "./useEntitlements";


export function UpgradeModal(): JSX.Element | null {
  const prompt = useUpgradePrompt((s) => s.prompt);
  const close = useUpgradePrompt((s) => s.close);
  if (!prompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="upgrade-modal"
        data-testid="upgrade-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-40 grid place-items-center bg-[var(--md-ink-0)]/70 backdrop-blur-md px-4"
      >
        <motion.div
          initial={{ y: 12, scale: 0.98, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.2, 0.7, 0.3, 1] }}
          className="md-panel--glass relative w-full max-w-lg rounded-2xl md-glow shadow-[var(--shadow-lg)]"
        >
          <button
            onClick={close}
            data-testid="upgrade-modal-close"
            aria-label="Dismiss"
            className="absolute right-4 top-4 rounded-full p-2 text-[var(--md-fog)] hover:bg-white/6 hover:text-[var(--md-ivory-0)]"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="grid gap-5 p-8">
            <div className="md-eyebrow md-eyebrow--amber">Marketing capacity</div>
            <h2 className="md-h1 text-3xl">{prompt.title}</h2>
            <p className="text-sm text-[var(--md-fog)]">{prompt.reason}</p>
            <div className="mt-2 grid gap-2">
              <Link
                to={prompt.upgrade_target ? `/pricing?highlight=${prompt.upgrade_target}` : "/pricing"}
                data-testid="upgrade-modal-plan"
                className="md-btn md-btn--primary"
                style={{ padding: "12px 22px", fontSize: 14 }}
              >
                Upgrade my plan <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              {prompt.addon_kind && (
                <Link
                  to={`/pricing?addon=${prompt.addon_kind}#add-ons`}
                  data-testid="upgrade-modal-addon"
                  className="md-btn md-btn--ghost"
                  style={{ padding: "12px 22px", fontSize: 14 }}
                >
                  Buy an add-on pack
                </Link>
              )}
              <button
                type="button"
                onClick={close}
                data-testid="upgrade-modal-wait"
                className="md-btn md-btn--ghost"
                style={{ padding: "12px 22px", fontSize: 14 }}
              >
                Wait for monthly reset
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
